import { useState, useMemo } from 'react'
import { Input } from '@neo/primitives/input'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { useParams } from 'router-v6'
import { queryClient } from 'v2/configs/rq-setup'
import type { Channels, Team } from 'v2/types/api/conversations'
import { SuspenseLoader } from 'v2/shared/components/suspense-loader'
import { conversationQueryKeys } from 'v2/shared/utils/query-key-management'
import { EmptyState } from 'v2/shared/components/empty-state'
import { useLiveTeamsQuery } from '../../queries'
import { useTransferMutations } from '../../mutations/conversations'
import { TeamItem } from './team-item'
import type { TransferSuccessProps } from './types'
import { getConversationForTransfer } from './utils'

interface TeamListProps extends TransferSuccessProps {
  channel?: Channels
}

export const TeamList = ({ channel, onTransferSuccess }: TeamListProps) => {
  const { data: teams, isLoading: teamsLoading, error } = useLiveTeamsQuery(channel)
  const [searchTerm, setSearchTerm] = useState('')
  const { conversationId } = useParams()
  const { transfer } = useTransferMutations()

  const filteredTeams = useMemo(() => {
    if (!teams) return []
    return teams
      .filter((team) => team.agent_count > 0)
      .filter((team) => team.name.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [teams, searchTerm])

  const handleTransfer = (team: Team, type: 'cold' | 'warm') => {
    const conversation = getConversationForTransfer(conversationId, channel)
    if (!conversation.child?.channel || !conversation.parent?.contact_uuid) return

    transfer.mutate(
      {
        channel: conversation.child?.channel,
        transferType: type,
        contactUuid: conversation.parent?.contact_uuid,
        identifier: team.uuid,
        agentOrTeam: 'team',
      },
      {
        onSuccess: () => {
          onTransferSuccess({
            name: team.name,
            identifier: team.uuid,
            participantType: 'team',
            type,
          })
        },
      },
    )
  }

  if (teamsLoading)
    return (
      <div className="relative h-[352px]">
        <SuspenseLoader text="Fetching available teams..." />
      </div>
    )

  if (error)
    return (
      <EmptyState
        type="app_error"
        text="Error fetching teams"
        actions={[
          {
            text: 'Retry',
            onClick: () => {
              queryClient.resetQueries({
                queryKey: conversationQueryKeys().liveTeams(channel),
              })
            },
          },
        ]}
      />
    )

  return (
    <div>
      <div className="px-1">
        <Input
          type="text"
          placeholder="Search teams"
          className="w-full mb-2"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="input-team-search"
        />
      </div>
      <ScrollArea className="flex max-h-[300px] flex-col overflow-y-auto -mr-4">
        <div className="pr-4">
          {filteredTeams.map((team) => (
            <TeamItem
              key={team.uuid}
              team={team}
              onTransfer={(type) => handleTransfer(team, type)}
              isDisabled={transfer.isPending}
            />
          ))}
          {filteredTeams.length === 0 && (
            <div className="text-s-400 text-tertiary truncate">No teams found</div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
